import type {
  MetaFunction,
  LoaderFunctionArgs,
  ActionFunctionArgs,
} from 'react-router'
import { useRef, useEffect } from 'react'
import { Form, Link, useLoaderData, useNavigation } from 'react-router'
import { data, redirect } from 'react-router'
import { useHydrated } from 'remix-utils/use-hydrated'
import { AuthenticityTokenInput } from 'remix-utils/csrf/react'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { Loader2 } from 'lucide-react'
import { siteConfig } from '#app/utils/constants/brand'
import {
  clearSelfManagedOnboardingSetCookieString,
  getSelfManagedOnboardingFromRequestCookie,
  setSelfManagedOnboardingSetCookieString,
} from '#app/utils/self-managed-onboarding-cookie'
import { Input } from '#app/components/ui/input'
import { Button } from '#app/components/ui/button'
import { ROUTE_PATH as DASHBOARD_PATH } from '#app/routes/dashboard+/_layout'
import { ROUTE_PATH as AUTH_VERIFY_PATH } from '#app/routes/auth+/verify'

export const ROUTE_PATH = '/auth/login' as const

export const LoginSchema = z.object({
  email: z.string().max(256).email('Email address is not valid.'),
})

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const titleSuffix = data?.intentSetup ? 'Set up association' : 'Login'
  return [{ title: `${siteConfig.siteTitle} - ${titleSuffix}` }]
}

const REDIRECT_AFTER_LOGIN_COOKIE = 'auth_redirect_after_login'

function getSafeRedirectTo(redirectTo: string | null): string | null {
  if (!redirectTo || !redirectTo.startsWith('/') || redirectTo.startsWith('//')) return null
  return redirectTo
}

export async function loader({ request }: LoaderFunctionArgs) {
  const { authenticator } = await import('#app/modules/auth/auth.server')
  const { getSession, commitSession, OAUTH2_STATE_SESSION_KEY } = await import(
    '#app/modules/auth/auth-session.server'
  )
  const url = new URL(request.url)
  const redirectTo = getSafeRedirectTo(url.searchParams.get('redirectTo'))

  const user = await authenticator.isAuthenticated(request)
  if (user) {
    const existingRedirect = request.headers.get('Cookie')?.split(';').find(c => c.trim().startsWith(`${REDIRECT_AFTER_LOGIN_COOKIE}=`))
    const afterLogin = existingRedirect ? decodeURIComponent(existingRedirect.split('=')[1]?.trim() ?? '') : null
    const target =
      redirectTo ??
      getSafeRedirectTo(afterLogin) ??
      (getSelfManagedOnboardingFromRequestCookie(request.headers.get('Cookie')) ? '/setup-neighborhood' : null) ??
      DASHBOARD_PATH
    return redirect(target)
  }

  const intent = url.searchParams.get('intent')
  const oauthErrorParam = url.searchParams.get('oauth_error')
  const oauthError =
    oauthErrorParam != null && oauthErrorParam.trim() !== ''
      ? oauthErrorParam.trim().slice(0, 600)
      : null

  const cookie = await getSession(request.headers.get('Cookie'))
  cookie.unset(OAUTH2_STATE_SESSION_KEY)
  const authEmail = cookie.get('auth:email')
  const authError = cookie.get(authenticator.sessionErrorKey)

  const safeAuthError = authError && typeof authError === 'object' && 'message' in authError
    ? authError
    : { message: '' }

  const headers = new Headers()
  headers.append('Set-Cookie', await commitSession(cookie))
  if (redirectTo) {
    headers.append(
      'Set-Cookie',
      `${REDIRECT_AFTER_LOGIN_COOKIE}=${encodeURIComponent(redirectTo)}; Path=/; Max-Age=600; SameSite=Lax`
    )
  }

  headers.append(
    'Set-Cookie',
    intent === 'setup' ? setSelfManagedOnboardingSetCookieString() : clearSelfManagedOnboardingSetCookieString()
  )

  return data({
    authEmail,
    authError: safeAuthError,
    oauthError,
    intentSetup: intent === 'setup',
  } as const, { headers })
}

export async function action({ request }: ActionFunctionArgs) {
  const { authenticator } = await import('#app/modules/auth/auth.server')
  const url = new URL(request.url)
  const pathname = url.pathname

  try {
    const clonedRequest = request.clone()
    const formData = await clonedRequest.formData()
    const { validateCSRF } = await import('#app/utils/csrf.server')
    const { checkHoneypot } = await import('#app/utils/honeypot.server')
    await validateCSRF(formData, clonedRequest.headers)
    checkHoneypot(formData)

    const intent = formData.get('intent')
    const redirectAfterLogin =
      intent === 'setup' ? '/setup-neighborhood' : null

    const response = await authenticator.authenticate('TOTP', request, {
      successRedirect: AUTH_VERIFY_PATH,
      failureRedirect: pathname,
    })

    if (redirectAfterLogin && response instanceof Response && response.status >= 300 && response.status < 400) {
      const headers = new Headers(response.headers)
      headers.append(
        'Set-Cookie',
        `${REDIRECT_AFTER_LOGIN_COOKIE}=${encodeURIComponent(redirectAfterLogin)}; Path=/; Max-Age=600; SameSite=Lax`
      )
      return new Response(response.body, {
        status: response.status,
        headers,
      })
    }
    return response
  } catch (error) {
    if (error instanceof Response) {
      return error
    }

    console.error('Authentication error:', error)
    return data(
      { error: 'Authentication failed' },
      { status: 400 }
    )
  }
}

function LoginForm({
  authEmail,
  authError,
  oauthError,
  emailForm,
  email,
  isEmailSubmitting,
  isGoogleSubmitting,
  inputRef,
  isHydrated,
  intentSetup,
}: {
  authEmail: string | undefined
  authError: { message: string }
  oauthError: string | null
  emailForm: ReturnType<typeof useForm<z.infer<typeof LoginSchema>>>[1]
  email: ReturnType<typeof useForm<z.infer<typeof LoginSchema>>>[1]['email']
  isEmailSubmitting: boolean
  isGoogleSubmitting: boolean
  inputRef: React.RefObject<HTMLInputElement | null>
  isHydrated: boolean
  intentSetup: boolean
}) {
  const emailInputProps = getInputProps(email, { type: 'email' })

  useEffect(() => {
    isHydrated && inputRef.current?.focus()
  }, [isHydrated, inputRef])

  const heading = authEmail
    ? 'Check your email'
    : intentSetup
      ? 'Set up your association'
      : 'Welcome back!'

  const subtext = authEmail
    ? intentSetup
      ? 'We sent you a link. Open it on this device to verify your email and continue setting up your HOA on NeighborCloud.'
      : 'We sent you a link to sign in. Open it from this device to continue.'
    : intentSetup
      ? 'Use your email or Google to get started. If you are new, we will create your account—then you can onboard your association on NeighborCloud.'
      : 'Sign in with your email or Google to access your neighborhood.'

  return (
    <>
      <h1 className="text-3xl font-semibold text-center">{heading}</h1>
      <p className="text-base text-muted-foreground max-w-md text-center">{subtext}</p>

      {oauthError ? (
        <p className="mb-2 max-w-md rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive dark:text-destructive-foreground">
          {oauthError}
        </p>
      ) : null}

      <Form
        method="POST"
        autoComplete="off"
        className="flex w-full flex-col items-center gap-1 max-w-sm mx-auto"
        {...getFormProps(emailForm)}
      >
        <AuthenticityTokenInput />
        <HoneypotInputs />
        {intentSetup ? <input type="hidden" name="intent" value="setup" /> : null}

        <div className="flex w-full flex-col gap-1.5">
          <label htmlFor={emailInputProps.id} className="sr-only">
            Email
          </label>
          <Input
            placeholder="Email"
            ref={inputRef}
            defaultValue={authEmail ?? ''}
            className={`bg-transparent ${
              email.errors && 'border-destructive focus-visible:ring-destructive'
            }`}
            {...emailInputProps}
          />
        </div>

        <div className="flex flex-col w-full">
          {!authError.message && email.errors && (
            <span className="mb-2 text-sm text-destructive dark:text-destructive-foreground">
              {email.errors.join(' ')}
            </span>
          )}
          {!authEmail && authError.message && (
            <span className="mb-2 text-sm text-destructive dark:text-destructive-foreground">
              {authError.message}
            </span>
          )}
        </div>

        <Button type="submit" className="w-full">
          {isEmailSubmitting ? <Loader2 className="animate-spin" /> : 'Continue with Email'}
        </Button>
      </Form>

      <div className="relative flex w-full items-center justify-center">
        <span className="absolute w-full border-b border-border" />
        <span className="z-10 bg-card px-2 text-xs font-medium uppercase text-primary/60">
          OR
        </span>
      </div>

      {/* Native form: full document POST + browser 302 to Google. No honeypot here — managers/autofill
          often fill hidden `name__confirm` in a normal profile (works in incognito); OAuth is still
          protected by CSRF token + Google's OAuth state. */}
      <form method="post" action="/auth/google" className="w-full max-w-sm">
        <AuthenticityTokenInput />
        <Button type="submit" variant="outline" className="w-full">
          {isGoogleSubmitting ? <Loader2 className="animate-spin" /> : 'Sign in with Google'}
        </Button>
      </form>

      <p className="px-4 text-center text-sm text-muted-foreground">
        By signing in, you agree to our{' '}
        <Link to="/tos" className="underline hover:text-primary">Terms of Service</Link>
        {' '}and{' '}
        <Link to="/privacy" className="underline hover:text-primary">Privacy Policy</Link>.
      </p>
    </>
  )
}

function isGoogleOAuthFormAction(formAction: string | undefined): boolean {
  if (!formAction) return false
  try {
    const pathname = new URL(formAction, 'https://local.invalid').pathname
    return pathname === '/auth/google'
  } catch {
    return formAction.includes('/auth/google')
  }
}

export default function Login() {
  const { authEmail, authError, oauthError, intentSetup } = useLoaderData<typeof loader>()
  const inputRef = useRef<HTMLInputElement>(null)
  const isHydrated = useHydrated()
  const navigation = useNavigation()
  const isGoogleSubmitting =
    navigation.state === 'submitting' &&
    isGoogleOAuthFormAction(navigation.formAction)
  const isEmailSubmitting =
    navigation.state === 'submitting' && !isGoogleSubmitting

  const [emailForm, { email }] = useForm({
    id: 'auth-login-email-form',
    constraint: getZodConstraint(LoginSchema),
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: LoginSchema })
    },
  })

  return (
    <div className="flex flex-col items-center justify-center gap-6 p-10 w-full max-w-lg mx-auto min-h-[80vh]">
      <LoginForm
        authEmail={authEmail}
        authError={authError}
        oauthError={oauthError}
        emailForm={emailForm}
        email={email}
        isEmailSubmitting={isEmailSubmitting}
        isGoogleSubmitting={isGoogleSubmitting}
        inputRef={inputRef}
        isHydrated={isHydrated}
        intentSetup={intentSetup}
      />
      {!intentSetup && (
        <p className="text-sm text-muted-foreground text-center">
          Setting up a new neighborhood?{' '}
          <Link to="/auth/setup" className="underline hover:text-primary">
            Sign in here
          </Link>
          .
        </p>
      )}
      <p className="mt-auto pt-6 text-center text-sm text-muted-foreground">
        Need help?{' '}
        <a
          href={siteConfig.helpDeskUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:text-primary"
        >
          Visit the Help Desk
        </a>
      </p>
    </div>
  )
}
